import { useState } from "react";
import { motion } from "framer-motion";
import { IMAGES, PRODUCT_SLOTS } from "@/data/content";
import { EASE, Eyebrow, MaskedLines } from "./shared";

// Product slots are placeholders — each entry already has the fields a real
// product needs (name, category, description, image) so the range can drop
// straight in when supplied.

export default function Products() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="bg-white py-28 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Eyebrow>Products</Eyebrow>
        <h2 className="mt-7 font-serif font-medium leading-[1.0] tracking-tight text-navy">
          <MaskedLines
            lines={[
              <span key="a" style={{ fontSize: "clamp(2.6rem, 5.6vw, 5.4rem)" }} className="block">
                THE RANGE,
              </span>,
              <span
                key="b"
                style={{ fontSize: "clamp(2.6rem, 5.6vw, 5.4rem)" }}
                className="block font-normal italic text-ink/80"
              >
                up close.
              </span>,
            ]}
          />
        </h2>

        <div className="mt-16 grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:sticky lg:top-24 lg:col-span-7 lg:h-[74vh] lg:self-start">
            <div
              className="img-grade relative h-[48vh] w-full overflow-hidden lg:h-full"
              data-testid="products-visual"
            >
              {PRODUCT_SLOTS.map((slot, i) => (
                <motion.img
                  key={slot.n}
                  src={IMAGES[slot.image]}
                  alt={`${slot.category} product placeholder visual`}
                  loading="lazy"
                  initial={false}
                  animate={{
                    opacity: active === i ? 1 : 0,
                    scale: active === i ? 1 : 1.06,
                  }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ))}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-navy/55 to-transparent p-6 pt-20">
                <motion.span
                  key={active}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white"
                  data-testid="products-caption"
                >
                  {PRODUCT_SLOTS[active].category}
                </motion.span>
                <span className="font-serif text-lg italic text-white/80">
                  {PRODUCT_SLOTS[active].n} / 0{PRODUCT_SLOTS.length}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            {PRODUCT_SLOTS.map((slot, i) => (
              <motion.div
                key={slot.n}
                onViewportEnter={() => setActive(i)}
                viewport={{ margin: "-42% 0px -42% 0px" }}
                className="flex min-h-[42vh] flex-col justify-center border-b border-softblue py-14 last:border-b-0 lg:min-h-[68vh]"
                data-testid={`product-slot-${slot.n}`}
              >
                <span className="font-serif text-xl italic text-brand">
                  {slot.n}
                </span>
                <div className="mt-5 overflow-hidden">
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                  >
                    <motion.h3
                      variants={{ hidden: { y: "105%" }, visible: { y: "0%" } }}
                      transition={{ duration: 0.9, ease: EASE }}
                      className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-navy md:text-5xl"
                    >
                      {slot.category}
                    </motion.h3>
                  </motion.div>
                </div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
                  className="mt-3 text-[11px] font-semibold uppercase tracking-[0.26em] text-fog/80"
                >
                  {slot.name}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                  className="mt-5 max-w-md text-base leading-relaxed text-fog"
                >
                  {slot.description}
                </motion.p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
